import {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import {
  Application,
  Container,
  Graphics,
  RenderTexture,
  Text,
  TextStyle,
} from 'pixi.js';
import type { TableConfig } from '@/components/table-planner/TableConfigModal';
import {
  hexToNumber,
  getSeatRadius,
  getSeatNumberPositions,
  hitTestPlanner,
  longEdgeHorizontalStaggerY,
  SEAT_COUNTER_BUTTON_SIZE_PX,
  TABLE_PLANNER_METRIC_STEP,
} from './pixiTablePlannerDrawing';
import { guestSeatMultilineLabel } from '@/lib/seatLabelUtils';

/* ------------------------------------------------------------------ */
/*  Module-level caches                                                */
/* ------------------------------------------------------------------ */

const _hexCache = new Map<string, number>();
function hex(h: string): number {
  let v = _hexCache.get(h);
  if (v === undefined) {
    v = hexToNumber(h);
    _hexCache.set(h, v);
  }
  return v;
}

const _styleCache = new Map<string, TextStyle>();
/** Cap DPR for balance of sharpness vs pan cost; RAF-coalesced render keeps interaction smooth. */
const MAX_PLANNER_RESOLUTION = 2;

const PLANNER_SURFACE = {
  light: { floor: '#f3f4f6', grid: '#e5e7eb', canvas: '#f1f5f9' },
  dark: { floor: '#0f172a', grid: '#334155', canvas: '#0f172a' },
} as const;

function cachedStyle(
  key: string,
  opts: ConstructorParameters<typeof TextStyle>[0]
): TextStyle {
  let s = _styleCache.get(key);
  if (s) return s;
  s = new TextStyle(opts);
  _styleCache.set(key, s);
  return s;
}

/** Width/height in floor units; 10 = 0.1 m, same as TableConfigModal. */
function snapPlannerSize(raw: number, snapToGrid: boolean, min = 80): number {
  const v = Math.round(raw);
  if (!snapToGrid) {
    return Math.max(min, v);
  }
  return Math.max(
    min,
    Math.round(v / TABLE_PLANNER_METRIC_STEP) * TABLE_PLANNER_METRIC_STEP
  );
}

function snapPlannerDiameter(
  raw: number,
  snapToGrid: boolean,
  min: number,
  max: number
): number {
  const v = Math.round(raw);
  const stepped = snapToGrid
    ? Math.round(v / TABLE_PLANNER_METRIC_STEP) * TABLE_PLANNER_METRIC_STEP
    : v;
  return Math.max(min, Math.min(max, stepped));
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** Minimal Konva Stage surface used by VisualTablePlannerPage (pointer, pan, PDF). */
export type TablePlannerStageShim = {
  container: () => HTMLDivElement;
  getPointerPosition: () => { x: number; y: number } | null;
  x: (v?: number) => number;
  y: (v?: number) => number;
  scaleX: (v?: number) => number;
  scaleY: (v?: number) => number;
  toDataURL: (config: {
    pixelRatio?: number;
    mimeType?: string;
    quality?: number;
    /** When true, floor grid lines are omitted from the bitmap (table plan only). */
    omitGrid?: boolean;
    /** When true, floor fill hidden and export composited on white (plan only). */
    omitBackground?: boolean;
    /**
     * World-space rect to export. Renderer is temporarily resized to this rect
     * at scale=1 so the output is fully independent of the current viewport.
     */
    frame?: { x: number; y: number; width: number; height: number };
  }) => string;
};

export type PixiTablePlannerCanvasProps = {
  playableSize: number;
  stageWidth: number;
  stageHeight: number;
  scale: number;
  stagePos: { x: number; y: number };
  tables: TableConfig[];
  selectedTableIds: Set<string>;
  highlightedRelatedGuests: Set<string>;
  showGrid: boolean;
  gridSize: number;
  /** When true, width/height while resizing snap to 0.1 m (same step as table settings). */
  snapToGrid: boolean;
  reduceDropShadow: boolean;
  useLocalDragPosition: boolean;
  isDraggingTable: boolean;
  isDraggingSeat: boolean;
  /** While dragging a guest from a seat, that source seat is drawn empty until drop completes. */
  seatDragSource: { tableId: string; seatNumber: number } | null;
  getGuestName: (rsvpId: string | null) => string | null;
  /** Per-guest seat colours; guests who share a host (invitedBy) should share the same colours. */
  getGuestSeatColours?: (rsvpId: string) => {
    fill: string;
    stroke: string;
    secondaryFill?: string;
    secondaryStroke?: string;
  } | null;
  /** Per-guest border colour based on their prefix/rank. */
  getGuestPrefixBorderColour?: (rsvpId: string) => string | null;
  onSelect: (tableId: string, event?: MouseEvent) => void;
  onDragStart: (tableId: string) => void;
  onDragMove: (tableId: string, x: number, y: number) => void;
  onDragEnd: (tableId: string, x: number, y: number) => void;
  onDoubleClick: (tableId: string) => void;
  onSeatClick: (tableId: string, seatNumber: number) => void;
  onSeatDragStart: (
    tableId: string,
    seatNumber: number,
    guestId: string
  ) => void;
  onSeatDrop: (tableId: string, seatNumber: number) => void;
  /** Pointer released after a seat drag but not over a valid target seat (clears drag UI). */
  onSeatDragCancel?: () => void;
  onResize: (
    tableId: string,
    width: number,
    height: number,
    x: number,
    y: number
  ) => void;
  onRotate: (tableId: string, rotation: number) => void;
  onAdjustSide: (
    tableId: string,
    side: 'top' | 'bottom' | 'left' | 'right' | 'leftEnd' | 'rightEnd',
    delta: number
  ) => void;
  onViewportChange: (x: number, y: number) => void;
  onViewportPanStart: () => void;
  onClearSelection: () => void;
  registerTableGroup: (id: string, node: KonvaGroupShim | null) => void;
  /** Floor and grid colours; keep in sync with app colour theme. */
  surfaceScheme?: 'light' | 'dark';
};

export type KonvaGroupShim = {
  x: (v?: number) => number;
  y: (v?: number) => number;
  getLayer: () => { batchDraw: () => void } | null;
};

function makeGroupShim(
  tableRoot: Container,
  requestRender: () => void
): KonvaGroupShim {
  return {
    x(v?: number) {
      if (v !== undefined) {
        tableRoot.position.x = v;
      }
      return tableRoot.position.x;
    },
    y(v?: number) {
      if (v !== undefined) {
        tableRoot.position.y = v;
      }
      return tableRoot.position.y;
    },
    getLayer() {
      return { batchDraw: requestRender };
    },
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PixiTablePlannerCanvas = forwardRef<
  TablePlannerStageShim,
  PixiTablePlannerCanvasProps
>(function PixiTablePlannerCanvas(props, ref) {
  const propsRef = useRef(props);
  propsRef.current = props;

  const {
    playableSize,
    stageWidth,
    stageHeight,
    scale,
    stagePos,
    tables,
    selectedTableIds,
    showGrid,
    gridSize,
    isDraggingSeat,
    isDraggingTable,
    seatDragSource,
    getGuestSeatColours,
    getGuestPrefixBorderColour,
    getGuestName,
    highlightedRelatedGuests,
    registerTableGroup,
    surfaceScheme = 'light',
  } = props;

  const hostRef = useRef<HTMLDivElement>(null);
  const [pixiReady, setPixiReady] = useState(false);
  const appRef = useRef<Application | null>(null);
  const viewportRef = useRef<Container | null>(null);
  const worldRef = useRef<Container | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const stagePosRef = useRef(stagePos);
  const scaleRef = useRef(scale);
  stagePosRef.current = stagePos;
  scaleRef.current = scale;

  const dragOverrideRef = useRef<Map<string, { x: number; y: number }>>(
    new Map()
  );
  const interactionRef = useRef<{
    mode:
      | 'idle'
      | 'pan'
      | 'table'
      | 'seat'
      | 'rotate'
      | 'resizeCircle'
      | 'resizeEdge';
    pointerId: number | null;
    tableId: string | null;
    seatNumber: number | null;
    guestId: string | null;
    startWorld: { x: number; y: number };
    startStagePos: { x: number; y: number };
    startTablePos: Map<string, { x: number; y: number }>;
    seatDragStartScreen: { x: number; y: number } | null;
    seatDragStarted: boolean;
    resizeStart: {
      width: number;
      height: number;
      tableX: number;
      tableY: number;
    } | null;
    resizeEdge: 'n' | 's' | 'e' | 'w' | null;
    /** World-space pointer at edge-resize start; sizing uses delta from here (matches Konva EdgeResizeZone). */
    resizePointerStartWorld: { x: number; y: number } | null;
    lastClick: { t: number; target: string } | null;
    tempRot: number | null;
    tempSize: { width: number; height: number } | null;
  }>({
    mode: 'idle',
    pointerId: null,
    tableId: null,
    seatNumber: null,
    guestId: null,
    startWorld: { x: 0, y: 0 },
    startStagePos: { x: 0, y: 0 },
    startTablePos: new Map(),
    seatDragStartScreen: null,
    seatDragStarted: false,
    resizeStart: null,
    resizeEdge: null,
    resizePointerStartWorld: null,
    lastClick: null,
    tempRot: null,
    tempSize: null,
  });

  const tableRootsRef = useRef<Map<string, Container>>(new Map());

  // --- Scene caching refs ---
  const bgGraphicsRef = useRef<Graphics | null>(null);
  /** `${playableSize}|${surfaceScheme}` invalidates floor fill when size or theme changes. */
  const floorVisualKeyRef = useRef('');
  const gridContainerRef = useRef<Graphics | null>(null);
  const gridKeyRef = useRef('');
  const tableCacheRef = useRef<
    Map<string, { container: Container; visualKey: string }>
  >(new Map());
  const renderRafRef = useRef<number | null>(null);

  const requestRender = useCallback(() => {
    if (renderRafRef.current !== null) {
      return;
    }
    renderRafRef.current = requestAnimationFrame(() => {
      renderRafRef.current = null;
      appRef.current?.render();
    });
  }, []);

  const screenToWorld = useCallback((sx: number, sy: number) => {
    const vp = viewportRef.current;
    if (!vp) return { x: 0, y: 0 };
    return {
      x: (sx - vp.position.x) / vp.scale.x,
      y: (sy - vp.position.y) / vp.scale.y,
    };
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Visual key: determines whether a table container must rebuild    */
  /* ---------------------------------------------------------------- */

  function computeTableKey(
    table: TableConfig,
    isSelected: boolean,
    sc: number,
    tempSize: { width: number; height: number } | null,
    tempSizeTableId: string | null,
    p: typeof props
  ): string {
    const tw =
      tempSize && tempSizeTableId === table.id ? tempSize.width : table.width;
    const th =
      tempSize && tempSizeTableId === table.id ? tempSize.height : table.height;
    const scaleBucket = Math.round(sc * 100);

    const seatParts: string[] = [];
    for (const s of table.seating) {
      if (!s || s.seatNumber === undefined) continue;
      const isSource =
        p.isDraggingSeat &&
        p.seatDragSource?.tableId === table.id &&
        p.seatDragSource?.seatNumber === s.seatNumber;
      const hl =
        s.rsvpId && p.highlightedRelatedGuests.has(s.rsvpId) ? '1' : '0';
      const colours =
        s.rsvpId && p.getGuestSeatColours
          ? p.getGuestSeatColours(s.rsvpId)
          : null;
      const prefixBorder =
        s.rsvpId && p.getGuestPrefixBorderColour
          ? (p.getGuestPrefixBorderColour(s.rsvpId) ?? '')
          : '';
      seatParts.push(
        `${s.seatNumber},${s.x},${s.y},${s.rsvpId ?? ''},${isSource ? '1' : '0'},${hl},${colours?.fill ?? ''},${colours?.stroke ?? ''},${colours?.secondaryFill ?? ''},${prefixBorder}`
      );
    }

    const rotBucket = Math.round((table.rotation ?? 0) * 10) / 10;
    return `${table.type}|${tw}|${th}|${table.color}|${table.chairColor ?? ''}|${table.name}|${table.seats}|${table.seatSize ?? ''}|${isSelected ? 1 : 0}|${scaleBucket}|${rotBucket}|${seatParts.join(';')}`;
  }

  /* ---------------------------------------------------------------- */
  /*  Build a single table's graphics                                  */
  /* ---------------------------------------------------------------- */

  function buildTableGraphics(
    table: TableConfig,
    isSelected: boolean,
    tableRotation: number,
    p: typeof props
  ): Container {
    const root = new Container();
    root.eventMode = 'static';

    const ir = interactionRef.current;
    const tw =
      ir.tempSize && ir.tableId === table.id ? ir.tempSize.width : table.width;
    const th =
      ir.tempSize && ir.tableId === table.id
        ? ir.tempSize.height
        : table.height;

    const sc = scaleRef.current;
    const strokeCol = isSelected ? hex('#3b82f6') : hex('#4a5568');
    const sw = isSelected ? 3 : 1;
    const fillCol = hex(table.color);

    // Table shape
    const shape = new Graphics();
    if (table.type === 'circular') {
      const r = Math.min(tw, th) / 2;
      shape.circle(0, 0, r).fill(fillCol).stroke({ width: sw, color: strokeCol });
    } else if (table.type === 'oval') {
      shape
        .ellipse(0, 0, tw / 2, th / 2)
        .fill(fillCol)
        .stroke({ width: sw, color: strokeCol });
    } else {
      shape
        .rect(-tw / 2, -th / 2, tw, th)
        .fill(fillCol)
        .stroke({ width: sw, color: strokeCol });
    }
    shape.eventMode = 'none';
    root.addChild(shape);

    // Table name label
    const labelFs = 14 / sc;
    const labelKey = `lbl-${Math.round(labelFs * 10)}`;
    const nameText = new Text({
      text: table.name,
      style: cachedStyle(labelKey, {
        fontFamily: 'system-ui, Segoe UI, sans-serif',
        fontSize: labelFs,
        fill: '#000000',
        fontWeight: 'bold',
        align: 'center',
      }),
    });
    nameText.anchor.set(0.5);
    nameText.position.set(0, 0);
    nameText.eventMode = 'none';
    root.addChild(nameText);

    // Seats
    const seatR = getSeatRadius(table);
    const numPos = getSeatNumberPositions(table, seatR);

    for (const seat of table.seating) {
      if (!seat || seat.seatNumber === undefined) continue;

      const pixiHidesSource =
        ir.seatDragStarted &&
        ir.mode === 'seat' &&
        ir.tableId === table.id &&
        ir.seatNumber === seat.seatNumber;
      const reactHidesSource =
        p.isDraggingSeat &&
        p.seatDragSource !== null &&
        p.seatDragSource.tableId === table.id &&
        p.seatDragSource.seatNumber === seat.seatNumber;
      const hideSourceSeat = pixiHidesSource || reactHidesSource;
      const guestName = hideSourceSeat ? null : p.getGuestName(seat.rsvpId);
      const occupied = !!guestName;
      const defaultChair = table.chairColor || '#e5e7eb';
      const isRel = !!(
        guestName &&
        seat.rsvpId &&
        p.highlightedRelatedGuests.has(seat.rsvpId)
      );
      const party =
        seat.rsvpId && p.getGuestSeatColours
          ? p.getGuestSeatColours(seat.rsvpId)
          : null;
      const prefixBorderColour =
        occupied && seat.rsvpId && p.getGuestPrefixBorderColour
          ? p.getGuestPrefixBorderColour(seat.rsvpId)
          : null;

      let chairFill: string;
      let chairStroke: string;
      let seatStrokeW = 2;
      if (!occupied) {
        chairFill = defaultChair;
        chairStroke = '#9ca3af';
      } else {
        if (party) {
          chairFill = party.fill;
          chairStroke = party.stroke;
        } else {
          chairFill = '#10b981';
          chairStroke = '#059669';
        }
        if (prefixBorderColour) {
          chairStroke = prefixBorderColour;
          seatStrokeW = 3;
        }
        if (isRel) {
          chairStroke = '#eab308';
          seatStrokeW = 4;
        }
      }

      const g = new Graphics();
      if (occupied && party?.secondaryFill) {
        const leftFill = party?.fill ?? chairFill;
        const rightFill = party?.secondaryFill ?? chairFill;
        g.moveTo(seat.x, seat.y)
          .arc(seat.x, seat.y, seatR, -Math.PI / 2, Math.PI / 2)
          .lineTo(seat.x, seat.y)
          .fill(hex(leftFill));
        g.moveTo(seat.x, seat.y)
          .arc(seat.x, seat.y, seatR, Math.PI / 2, (3 * Math.PI) / 2)
          .lineTo(seat.x, seat.y)
          .fill(hex(rightFill));
        g.circle(seat.x, seat.y, seatR).stroke({
          width: seatStrokeW,
          color: hex(chairStroke),
        });
      } else {
        g.circle(seat.x, seat.y, seatR)
          .fill(hex(chairFill))
          .stroke({ width: seatStrokeW, color: hex(chairStroke) });
      }
      g.eventMode = 'none';
      root.addChild(g);

      if (guestName) {
        const trimmed = guestName.trim();
        const displayText = guestSeatMultilineLabel(trimmed);

        // Keep label inside the seat circle (previously only height was checked, so long names overflowed horizontally).
        const innerMargin = seatStrokeW + 4;
        const maxBox = Math.max(14, seatR * 2 - innerMargin * 2);
        const wrapW = Math.min(Math.max(20, seatR * 1.5), maxBox);
        const maxH = maxBox;
        let fontSize = Math.max(6, 10 * (seatR / 28));

        const makeStyle = (fs: number) =>
          new TextStyle({
            fontFamily: 'system-ui, Segoe UI, sans-serif',
            fontSize: fs,
            fill: '#000000',
            fontWeight: 'bold',
            align: 'center',
            wordWrap: true,
            wordWrapWidth: wrapW,
            lineHeight: Math.ceil(fs * 1.12),
          });

        let nameTextSeat = new Text({
          text: displayText,
          style: makeStyle(fontSize),
        });
        while (
          (nameTextSeat.width > maxBox || nameTextSeat.height > maxH) &&
          fontSize > 3.5
        ) {
          fontSize -= 0.5;
          nameTextSeat.destroy();
          nameTextSeat = new Text({
            text: displayText,
            style: makeStyle(fontSize),
          });
        }

        nameTextSeat.anchor.set(0.5);
        nameTextSeat.position.set(seat.x, seat.y);
        nameTextSeat.rotation = -tableRotation;
        nameTextSeat.label = 'counter-rotate';
        nameTextSeat.eventMode = 'none';
        root.addChild(nameTextSeat);
      }

      const np = numPos.get(seat.seatNumber);
      if (np) {
        const snFs = 14 * (seatR / 28);
        const snKey = `sn-${Math.round(snFs * 10)}`;
        const sn = new Text({
          text: String(seat.seatNumber),
          style: cachedStyle(snKey, {
            fontFamily: 'system-ui, Segoe UI, sans-serif',
            fontSize: snFs,
            fill: '#000000',
            fontWeight: 'bold',
            align: 'center',
          }),
        });
        sn.anchor.set(0.5);
        sn.position.set(np.numberX, np.numberY);
        sn.rotation = -tableRotation;
        sn.label = 'counter-rotate';
        sn.eventMode = 'none';
        root.addChild(sn);
      }
    }

    // Selection UI (handles, resize zones, counters)
    if (isSelected) {
      const rotY = -th / 2 - 55 / sc;
      const rh = new Graphics();
      const lineLen = 30 / sc;
      const hs = 12 / sc;
      rh.moveTo(0, 0)
        .lineTo(0, -lineLen)
        .stroke({ width: 2 / sc, color: hex('#3b82f6') });
      rh.circle(0, -lineLen, hs)
        .fill(hex('#60a5fa'))
        .stroke({ width: 2 / sc, color: 0xffffff });
      rh.position.set(0, rotY + lineLen);
      rh.eventMode = 'static';
      rh.cursor = 'grab';
      root.addChild(rh);

      if (table.type === 'circular') {
        const radius = Math.min(tw, th) / 2;
        for (const angle of [0, 90, 180, 270]) {
          const rad = (angle * Math.PI) / 180;
          const hx = Math.cos(rad) * radius;
          const hy = Math.sin(rad) * radius;
          const h = new Graphics();
          h.circle(hx, hy, 12 / sc)
            .fill(hex('#3b82f6'))
            .stroke({ width: 2 / sc, color: 0xffffff });
          h.eventMode = 'static';
          h.cursor = 'nwse-resize';
          root.addChild(h);
        }
      } else {
        const thickness = 14 / sc;
        const edges: Array<{
          x: number;
          y: number;
          w: number;
          h: number;
          c: string;
        }> = [
          {
            x: -tw / 2,
            y: -th / 2 - thickness / 2,
            w: tw,
            h: thickness,
            c: 'ns-resize',
          },
          {
            x: -tw / 2,
            y: th / 2 - thickness / 2,
            w: tw,
            h: thickness,
            c: 'ns-resize',
          },
          {
            x: tw / 2 - thickness / 2,
            y: -th / 2,
            w: thickness,
            h: th,
            c: 'ew-resize',
          },
          {
            x: -tw / 2 - thickness / 2,
            y: -th / 2,
            w: thickness,
            h: th,
            c: 'ew-resize',
          },
        ];
        for (const e of edges) {
          const z = new Graphics();
          z.rect(e.x, e.y, e.w, e.h).fill({ color: 0x000000, alpha: 0.01 });
          z.eventMode = 'static';
          z.cursor = e.c;
          root.addChild(z);
        }
      }

      const buttonSize = SEAT_COUNTER_BUTTON_SIZE_PX / sc;
      const counterFs = Math.max(11, SEAT_COUNTER_BUTTON_SIZE_PX * 0.62) / sc;

      /**
       * Minus left, plus right. When rotateWithTable is true, the strip stays screen-horizontal
       * (ignores table rotation), matching side-by-side controls on long edges and wide top/bottom.
       */
      const renderCounterHorizontal = (
        x: number,
        y: number,
        rotateWithTable = false
      ) => {
        const counter = new Container();
        counter.position.set(x, y);
        if (rotateWithTable) {
          counter.rotation = -tableRotation;
        }
        const cbg = new Graphics();
        cbg
          .roundRect(
            -buttonSize * 1.2,
            -buttonSize / 2,
            buttonSize * 2.4,
            buttonSize,
            3 / sc
          )
          .fill(hex('#ffffff'))
          .stroke({ width: 1 / sc, color: hex('#d1d5db') });
        cbg.eventMode = 'none';
        counter.addChild(cbg);

        const counterKey = `cnt-${Math.round(counterFs * 10)}`;
        const minus = new Text({
          text: '-',
          style: cachedStyle(counterKey, {
            fontFamily: 'system-ui, Segoe UI, sans-serif',
            fontSize: counterFs,
            fill: '#111827',
            fontWeight: 'bold',
            align: 'center',
          }),
        });
        minus.anchor.set(0.5);
        minus.position.set(-buttonSize * 0.55, 0);
        minus.eventMode = 'none';
        counter.addChild(minus);

        const plus = new Text({
          text: '+',
          style: cachedStyle(counterKey, {
            fontFamily: 'system-ui, Segoe UI, sans-serif',
            fontSize: counterFs,
            fill: '#111827',
            fontWeight: 'bold',
            align: 'center',
          }),
        });
        plus.anchor.set(0.5);
        plus.position.set(buttonSize * 0.55, 0);
        plus.eventMode = 'none';
        counter.addChild(plus);
        root.addChild(counter);
      };

      /** Plus above, minus below along local Y (short edges of a rectangle, or square sides paired with horizontal long edges). */
      const renderCounterVertical = (x: number, y: number) => {
        const counter = new Container();
        counter.position.set(x, y);
        const cbg = new Graphics();
        const rx = 4 / sc;
        const bw = buttonSize;
        const bh = buttonSize * 2.4;
        const bx = -bw / 2;
        const by = -bh / 2;
        cbg
          .roundRect(bx, by, bw, bh, rx)
          .fill(hex('#ffffff'))
          .stroke({ width: Math.max(1 / sc, 0.75), color: hex('#d1d5db') });
        cbg.eventMode = 'none';
        counter.addChild(cbg);

        const counterKey = `cntv-${Math.round(counterFs * 10)}`;
        const plus = new Text({
          text: '+',
          style: cachedStyle(counterKey, {
            fontFamily: 'system-ui, Segoe UI, sans-serif',
            fontSize: counterFs,
            fill: '#111827',
            fontWeight: 'bold',
            align: 'center',
          }),
        });
        plus.anchor.set(0.5);
        plus.position.set(0, -buttonSize * 0.55);
        plus.eventMode = 'none';
        counter.addChild(plus);

        const minus = new Text({
          text: '-',
          style: cachedStyle(counterKey, {
            fontFamily: 'system-ui, Segoe UI, sans-serif',
            fontSize: counterFs,
            fill: '#111827',
            fontWeight: 'bold',
            align: 'center',
          }),
        });
        minus.anchor.set(0.5);
        minus.position.set(0, buttonSize * 0.55);
        minus.eventMode = 'none';
        counter.addChild(minus);
        root.addChild(counter);
      };

      const insetFromEdge = 14 / sc;
      const insetNudge = 6 / sc;
      /** Top/bottom edges are longer when width >= height; otherwise left/right are the long edges. */
      const longEdgesAreTopBottom = tw >= th;
      const lhStagger = longEdgeHorizontalStaggerY(tw, th, sc);

      if (table.type === 'circular') {
        renderCounterHorizontal(0, tw / 2 + 25 / sc);
      } else if (table.type === 'rectangular') {
        if (longEdgesAreTopBottom) {
          renderCounterHorizontal(0, -th / 2 + insetFromEdge + insetNudge, true);
          renderCounterHorizontal(0, th / 2 - insetFromEdge - insetNudge, true);
          renderCounterVertical(-tw / 2 + insetFromEdge + insetNudge, 0);
          renderCounterVertical(tw / 2 - insetFromEdge - insetNudge, 0);
        } else {
          renderCounterVertical(0, -th / 2 + insetFromEdge + insetNudge);
          renderCounterVertical(0, th / 2 - insetFromEdge - insetNudge);
          renderCounterHorizontal(
            -tw / 2 + insetFromEdge + insetNudge,
            -lhStagger,
            true
          );
          renderCounterHorizontal(
            tw / 2 - insetFromEdge - insetNudge,
            lhStagger,
            true
          );
        }
      } else if (table.type === 'square') {
        const sideY = -th / 4;
        if (longEdgesAreTopBottom) {
          renderCounterHorizontal(0, -th / 2 + insetFromEdge + insetNudge, true);
          renderCounterHorizontal(0, th / 2 - insetFromEdge - insetNudge, true);
          renderCounterVertical(-tw / 2 + insetFromEdge + insetNudge, sideY);
          renderCounterVertical(tw / 2 - insetFromEdge - insetNudge, sideY);
        } else {
          renderCounterVertical(0, -th / 2 + insetFromEdge + insetNudge);
          renderCounterVertical(0, th / 2 - insetFromEdge - insetNudge);
          renderCounterHorizontal(
            -tw / 2 + insetFromEdge + insetNudge,
            sideY - lhStagger,
            true
          );
          renderCounterHorizontal(
            tw / 2 - insetFromEdge - insetNudge,
            sideY + lhStagger,
            true
          );
        }
      }
    }

    return root;
  }

  /* ---------------------------------------------------------------- */
  /*  Incremental scene sync (replaces monolithic drawWorld)           */
  /* ---------------------------------------------------------------- */

  const syncScene = useCallback(() => {
    const world = worldRef.current;
    const app = appRef.current;
    if (!world || !app) return;

    const p = propsRef.current;
    const sc = scaleRef.current;
    const ir = interactionRef.current;

    const scheme = p.surfaceScheme === 'dark' ? 'dark' : 'light';
    const surface = PLANNER_SURFACE[scheme];
    const floorKey = `${p.playableSize}|${scheme}`;

    // 1. Background (rebuilt when playable size or surface scheme changes)
    if (!bgGraphicsRef.current || floorVisualKeyRef.current !== floorKey) {
      if (bgGraphicsRef.current) {
        bgGraphicsRef.current.destroy();
      }
      const bg = new Graphics();
      bg.rect(0, 0, p.playableSize, p.playableSize).fill({
        color: hex(surface.floor),
      });
      bg.eventMode = 'static';
      bg.cursor = 'grab';
      bgGraphicsRef.current = bg;
      floorVisualKeyRef.current = floorKey;
      world.addChildAt(bg, 0);
    }

    // 2. Grid (only rebuilt when grid settings, scale, or surface scheme change)
    const gk = `${p.showGrid}|${p.gridSize}|${Math.round(sc * 100)}|${scheme}`;
    if (gk !== gridKeyRef.current) {
      if (gridContainerRef.current) {
        gridContainerRef.current.destroy();
        gridContainerRef.current = null;
      }
      if (p.showGrid) {
        const grid = new Graphics();
        const lineW = Math.max(1 / sc, 0.5);
        for (let i = 0; i <= p.playableSize / p.gridSize; i++) {
          grid
            .moveTo(i * p.gridSize, 0)
            .lineTo(i * p.gridSize, p.playableSize)
            .stroke({ width: lineW, color: hex(surface.grid) });
        }
        for (let i = 0; i <= p.playableSize / p.gridSize; i++) {
          grid
            .moveTo(0, i * p.gridSize)
            .lineTo(p.playableSize, i * p.gridSize)
            .stroke({ width: lineW, color: hex(surface.grid) });
        }
        grid.eventMode = 'none';
        gridContainerRef.current = grid;
      }
      gridKeyRef.current = gk;
    }

    // Ensure bg is first child, grid is second
    const bgNode = bgGraphicsRef.current;
    if (bgNode && bgNode.parent !== world) {
      world.addChildAt(bgNode, 0);
    }
    const gridNode = gridContainerRef.current;
    if (gridNode) {
      if (gridNode.parent !== world) {
        world.addChildAt(gridNode, bgNode ? 1 : 0);
      }
    }

    // 3. Tables: incremental diff
    const sorted = [...p.tables].sort((a, b) => {
      const sa = p.selectedTableIds.has(a.id) ? 1 : 0;
      const sb = p.selectedTableIds.has(b.id) ? 1 : 0;
      return sa - sb;
    });

    const currentIds = new Set(p.tables.map((t) => t.id));
    const cache = tableCacheRef.current;

    // Remove stale table containers
    for (const [id, cached] of cache) {
      if (!currentIds.has(id)) {
        cached.container.destroy({ children: true });
        cache.delete(id);
        tableRootsRef.current.delete(id);
        p.registerTableGroup(id, null);
      }
    }

    // Update or create table containers
    for (const table of sorted) {
      const isSel = p.selectedTableIds.has(table.id);
      const newKey = computeTableKey(
        table,
        isSel,
        sc,
        ir.tempSize,
        ir.tableId,
        p
      );
      const cached = cache.get(table.id);

      const ov = dragOverrideRef.current.get(table.id);
      const tx = ov?.x ?? table.x;
      const ty = ov?.y ?? table.y;
      const rot = (table.rotation * Math.PI) / 180;

      if (cached && cached.visualKey === newKey) {
        // No visual change: just update position/rotation and ensure z-order
        cached.container.position.set(tx, ty);
        cached.container.rotation = rot;
        world.addChild(cached.container);
      } else {
        // Visual change: rebuild this table
        if (cached) {
          cached.container.destroy({ children: true });
          tableRootsRef.current.delete(table.id);
          p.registerTableGroup(table.id, null);
        }

        const tableRoot = new Container();
        tableRoot.label = `table-${table.id}`;
        tableRoot.position.set(tx, ty);
        tableRoot.rotation = rot;
        tableRoot.eventMode = 'static';

        const content = buildTableGraphics(table, isSel, rot, p);
        while (content.children.length > 0) {
          tableRoot.addChild(content.children[0]);
        }
        content.destroy();

        world.addChild(tableRoot);
        tableRootsRef.current.set(table.id, tableRoot);
        cache.set(table.id, { container: tableRoot, visualKey: newKey });
        p.registerTableGroup(
          table.id,
          makeGroupShim(tableRoot, requestRender)
        );
      }
    }
  }, [requestRender]);

  /* ---------------------------------------------------------------- */
  /*  Rebuild a single table (used during resize interactions)         */
  /* ---------------------------------------------------------------- */

  const rebuildSingleTable = useCallback(
    (tableId: string) => {
      const world = worldRef.current;
      if (!world) return;

      const p = propsRef.current;
      const sc = scaleRef.current;
      const ir = interactionRef.current;
      const table = p.tables.find((t) => t.id === tableId);
      if (!table) return;

      const isSel = p.selectedTableIds.has(table.id);
      const newKey = computeTableKey(
        table,
        isSel,
        sc,
        ir.tempSize,
        ir.tableId,
        p
      );

      const cache = tableCacheRef.current;
      const cached = cache.get(table.id);
      if (cached) {
        cached.container.destroy({ children: true });
        tableRootsRef.current.delete(table.id);
        p.registerTableGroup(table.id, null);
      }

      const ov = dragOverrideRef.current.get(table.id);
      const tx = ov?.x ?? table.x;
      const ty = ov?.y ?? table.y;
      const rot = (table.rotation * Math.PI) / 180;

      const tableRoot = new Container();
      tableRoot.label = `table-${table.id}`;
      tableRoot.position.set(tx, ty);
      tableRoot.rotation = rot;
      tableRoot.eventMode = 'static';

      const content = buildTableGraphics(table, isSel, rot, p);
      while (content.children.length > 0) {
        tableRoot.addChild(content.children[0]);
      }
      content.destroy();

      world.addChild(tableRoot);
      tableRootsRef.current.set(table.id, tableRoot);
      cache.set(table.id, { container: tableRoot, visualKey: newKey });
      p.registerTableGroup(
        table.id,
        makeGroupShim(tableRoot, requestRender)
      );
    },
    [requestRender]
  );

  /* ---------------------------------------------------------------- */
  /*  Application lifecycle                                            */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let destroyed = false;
    const app = new Application();

    (async () => {
      const initialScheme =
        propsRef.current.surfaceScheme === 'dark' ? 'dark' : 'light';
      await app.init({
        width: propsRef.current.stageWidth,
        height: propsRef.current.stageHeight,
        background: PLANNER_SURFACE[initialScheme].canvas,
        antialias: true,
        resolution: Math.min(window.devicePixelRatio || 1, MAX_PLANNER_RESOLUTION),
        autoDensity: true,
        preference: 'webgl',
        preserveDrawingBuffer: false,
      });
      if (destroyed) {
        app.destroy(true);
        return;
      }

      // Stop the automatic render ticker; we render manually.
      app.ticker.stop();

      host.appendChild(app.canvas as HTMLCanvasElement);
      appRef.current = app;

      const viewport = new Container();
      viewport.eventMode = 'static';
      app.stage.addChild(viewport);
      viewportRef.current = viewport;

      const world = new Container();
      world.eventMode = 'static';
      viewport.addChild(world);
      worldRef.current = world;

      syncScene();
      viewport.scale.set(scaleRef.current);
      viewport.position.set(stagePosRef.current.x, stagePosRef.current.y);
      app.render();
      setPixiReady(true);
    })();

    return () => {
      destroyed = true;
      setPixiReady(false);
      tableCacheRef.current.forEach((c) =>
        c.container.destroy({ children: true })
      );
      tableCacheRef.current.clear();
      if (renderRafRef.current !== null) {
        cancelAnimationFrame(renderRafRef.current);
        renderRafRef.current = null;
      }
      tableRootsRef.current.forEach((_, id) =>
        propsRef.current.registerTableGroup(id, null)
      );
      tableRootsRef.current.clear();
      bgGraphicsRef.current = null;
      floorVisualKeyRef.current = '';
      gridContainerRef.current = null;
      gridKeyRef.current = '';
      appRef.current?.destroy(true);
      appRef.current = null;
      viewportRef.current = null;
      worldRef.current = null;
    };
  }, []);

  // Trigger incremental sync when visual data changes
  useEffect(() => {
    if (!pixiReady || !appRef.current) return;
    syncScene();
    appRef.current.render();
  }, [
    pixiReady,
    tables,
    selectedTableIds,
    highlightedRelatedGuests,
    showGrid,
    gridSize,
    playableSize,
    surfaceScheme,
    isDraggingSeat,
    seatDragSource,
    getGuestSeatColours,
    getGuestPrefixBorderColour,
    getGuestName,
    scale,
    syncScene,
  ]);

  useEffect(() => {
    const app = appRef.current;
    if (!pixiReady || !app) return;
    const scheme = surfaceScheme === 'dark' ? 'dark' : 'light';
    const bgSys = app.renderer.background as unknown as { color: string };
    bgSys.color = PLANNER_SURFACE[scheme].canvas;
    app.render();
  }, [pixiReady, surfaceScheme]);

  // Viewport transform (cheap: no world rebuild)
  useEffect(() => {
    const app = appRef.current;
    const viewport = viewportRef.current;
    if (!pixiReady || !app || !viewport) return;
    app.renderer.resize(stageWidth, stageHeight);
    viewport.scale.set(scale);
    viewport.position.set(stagePos.x, stagePos.y);
    app.render();
  }, [pixiReady, stageWidth, stageHeight, scale, stagePos.x, stagePos.y]);

  // Track pointer position
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const onMove = (e: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      pointerRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    host.addEventListener('pointermove', onMove);
    return () => host.removeEventListener('pointermove', onMove);
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Pointer interaction handlers                                     */
  /* ---------------------------------------------------------------- */

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (!hostRef.current || !viewportRef.current) return;

      const p = propsRef.current;
      const rect = hostRef.current.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      pointerRef.current = { x: sx, y: sy };
      const world = screenToWorld(sx, sy);
      const hit = hitTestPlanner(
        world.x,
        world.y,
        p.tables,
        p.selectedTableIds,
        scaleRef.current
      );

      const ir = interactionRef.current;
      ir.pointerId = e.pointerId;

      if (hit.kind === 'adjustCounter') {
        p.onAdjustSide(
          hit.tableId,
          hit.side as
            | 'top'
            | 'bottom'
            | 'left'
            | 'right'
            | 'leftEnd'
            | 'rightEnd',
          hit.delta
        );
        e.preventDefault();
        return;
      }

      if (hit.kind === 'rotationHandle') {
        ir.mode = 'rotate';
        ir.tableId = hit.tableId;
        ir.startWorld = world;
        ir.tempRot = null;
        return;
      }

      if (hit.kind === 'resizeCorner' && hit.tableId) {
        const t = p.tables.find((x) => x.id === hit.tableId);
        if (t) {
          ir.mode = 'resizeCircle';
          ir.tableId = t.id;
          ir.resizeStart = {
            width: t.width,
            height: t.height,
            tableX: t.x,
            tableY: t.y,
          };
        }
        return;
      }

      if (hit.kind === 'resizeEdge' && hit.tableId) {
        const t = p.tables.find((x) => x.id === hit.tableId);
        if (t) {
          ir.mode = 'resizeEdge';
          ir.tableId = t.id;
          ir.resizeEdge = hit.edge;
          ir.resizeStart = {
            width: t.width,
            height: t.height,
            tableX: t.x,
            tableY: t.y,
          };
          ir.resizePointerStartWorld = { x: world.x, y: world.y };
        }
        return;
      }

      if (hit.kind === 'seat') {
        if (hit.rsvpId) {
          ir.mode = 'seat';
          ir.tableId = hit.tableId;
          ir.seatNumber = hit.seatNumber;
          ir.guestId = hit.rsvpId;
          ir.seatDragStartScreen = { x: e.clientX, y: e.clientY };
          ir.seatDragStarted = false;
        } else {
          p.onSeatClick(hit.tableId, hit.seatNumber);
        }
        e.preventDefault();
        return;
      }

      if (hit.kind === 'tableBody') {
        const tid = hit.tableId;
        const now = Date.now();
        const key = `t:${tid}`;
        if (
          ir.lastClick &&
          ir.lastClick.target === key &&
          now - ir.lastClick.t < 350
        ) {
          p.onDoubleClick(tid);
          ir.lastClick = null;
          return;
        }
        ir.lastClick = { t: now, target: key };

        p.onSelect(tid, e as unknown as MouseEvent);

        if (!p.isDraggingSeat) {
          ir.mode = 'table';
          ir.tableId = tid;
          ir.startWorld = world;
          const toMove = p.selectedTableIds.has(tid)
            ? Array.from(p.selectedTableIds)
            : [tid];
          ir.startTablePos = new Map();
          toMove.forEach((id) => {
            const t = p.tables.find((x) => x.id === id);
            const ov = dragOverrideRef.current.get(id);
            if (t) {
              ir.startTablePos.set(id, { x: ov?.x ?? t.x, y: ov?.y ?? t.y });
            }
          });
          dragOverrideRef.current.clear();
          p.onDragStart(tid);
        }
        return;
      }

      if (hit.kind === 'viewport') {
        p.onClearSelection();
      }

      if (!p.isDraggingTable && !p.isDraggingSeat) {
        p.onViewportPanStart();
        ir.mode = 'pan';
        ir.startWorld = { x: sx, y: sy };
        ir.startStagePos = { ...stagePosRef.current };
      }
    },
    [screenToWorld]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const ir = interactionRef.current;
      if (ir.pointerId !== null && e.pointerId !== ir.pointerId) return;
      if (!hostRef.current || !viewportRef.current) return;

      const p = propsRef.current;
      const rect = hostRef.current.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      pointerRef.current = { x: sx, y: sy };

      if (ir.mode === 'pan') {
        const dx = sx - ir.startWorld.x;
        const dy = sy - ir.startWorld.y;
        viewportRef.current.position.set(
          ir.startStagePos.x + dx,
          ir.startStagePos.y + dy
        );
        requestRender();
        return;
      }

      if (ir.mode === 'table' && ir.tableId) {
        const world = screenToWorld(sx, sy);
        const start = ir.startTablePos.get(ir.tableId);
        if (!start) return;
        const dx = world.x - ir.startWorld.x;
        const dy = world.y - ir.startWorld.y;

        if (p.useLocalDragPosition) {
          const nx = start.x + dx;
          const ny = start.y + dy;
          dragOverrideRef.current.set(ir.tableId, { x: nx, y: ny });
          const root = tableRootsRef.current.get(ir.tableId);
          if (root) root.position.set(nx, ny);
          p.onDragMove(ir.tableId, nx, ny);
        } else {
          ir.startTablePos.forEach((pos, id) => {
            const root = tableRootsRef.current.get(id);
            if (root) {
              const nx = pos.x + dx;
              const ny = pos.y + dy;
              root.position.set(nx, ny);
              dragOverrideRef.current.set(id, { x: nx, y: ny });
            }
          });
          p.onDragMove(ir.tableId, start.x + dx, start.y + dy);
        }
        requestRender();
        return;
      }

      if (
        ir.mode === 'seat' &&
        ir.seatDragStartScreen &&
        ir.guestId &&
        ir.tableId &&
        ir.seatNumber !== null
      ) {
        const ddx = e.clientX - ir.seatDragStartScreen.x;
        const ddy = e.clientY - ir.seatDragStartScreen.y;
        if (!ir.seatDragStarted && Math.sqrt(ddx * ddx + ddy * ddy) > 10) {
          p.onSeatDragStart(ir.tableId, ir.seatNumber, ir.guestId);
          ir.seatDragStarted = true;
          rebuildSingleTable(ir.tableId);
          requestRender();
        }
      }

      if (ir.mode === 'rotate' && ir.tableId) {
        const t = p.tables.find((x) => x.id === ir.tableId);
        if (!t) return;
        const w = screenToWorld(sx, sy);
        const ang =
          (Math.atan2(w.y - t.y, w.x - t.x) * 180) / Math.PI + 90;
        const snapped = Math.round(ang / 5) * 5;
        ir.tempRot = snapped;

        // Update container rotation directly (no parent state update)
        const root = tableRootsRef.current.get(ir.tableId);
        if (root) {
          const radians = (snapped * Math.PI) / 180;
          root.rotation = radians;
          // Counter-rotate text labels to keep them upright
          for (const child of root.children) {
            if (child.label === 'counter-rotate') {
              child.rotation = -radians;
            }
          }
          requestRender();
        }
        return;
      }

      if (ir.mode === 'resizeCircle' && ir.tableId && ir.resizeStart) {
        const t = p.tables.find((x) => x.id === ir.tableId);
        if (!t) return;
        const w = screenToWorld(sx, sy);
        const dist = Math.sqrt((w.x - t.x) ** 2 + (w.y - t.y) ** 2);
        const d = snapPlannerDiameter(
          dist * 2,
          p.snapToGrid,
          80,
          800
        );
        ir.tempSize = { width: d, height: d };
        rebuildSingleTable(ir.tableId);
        requestRender();
      }

      if (
        ir.mode === 'resizeEdge' &&
        ir.tableId &&
        ir.resizeStart &&
        ir.resizeEdge &&
        ir.resizePointerStartWorld
      ) {
        const t = p.tables.find((x) => x.id === ir.tableId);
        if (!t) return;
        const w = screenToWorld(sx, sy);
        const rs = ir.resizeStart;
        const pw = ir.resizePointerStartWorld;
        // Cumulative delta in world space, then rotate into table-local (same as VisualTable EdgeResizeZone).
        const dwx = w.x - pw.x;
        const dwy = w.y - pw.y;
        const cos = Math.cos((-t.rotation * Math.PI) / 180);
        const sin = Math.sin((-t.rotation * Math.PI) / 180);
        const deltaLocalX = dwx * cos - dwy * sin;
        const deltaLocalY = dwx * sin + dwy * cos;
        let nw = rs.width;
        let nh = rs.height;
        let pdx = 0;
        let pdy = 0;
        if (ir.resizeEdge === 'n') {
          nh = snapPlannerSize(rs.height - deltaLocalY, p.snapToGrid);
          pdy = -(nh - rs.height) / 2;
        } else if (ir.resizeEdge === 's') {
          nh = snapPlannerSize(rs.height + deltaLocalY, p.snapToGrid);
          pdy = (nh - rs.height) / 2;
        } else if (ir.resizeEdge === 'e') {
          nw = snapPlannerSize(rs.width + deltaLocalX, p.snapToGrid);
          pdx = (nw - rs.width) / 2;
        } else if (ir.resizeEdge === 'w') {
          nw = snapPlannerSize(rs.width - deltaLocalX, p.snapToGrid);
          pdx = -(nw - rs.width) / 2;
        }
        const pr = (t.rotation * Math.PI) / 180;
        const dxs = pdx * Math.cos(pr) - pdy * Math.sin(pr);
        const dys = pdx * Math.sin(pr) + pdy * Math.cos(pr);
        ir.tempSize = { width: nw, height: nh };
        dragOverrideRef.current.set(ir.tableId, {
          x: rs.tableX + dxs,
          y: rs.tableY + dys,
        });
        rebuildSingleTable(ir.tableId);
        requestRender();
      }
    },
    [screenToWorld, requestRender, rebuildSingleTable]
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const ir = interactionRef.current;
      if (ir.pointerId !== null && e.pointerId !== ir.pointerId) return;
      if (!viewportRef.current) return;

      const p = propsRef.current;

      if (ir.mode === 'pan') {
        p.onViewportChange(
          viewportRef.current.position.x,
          viewportRef.current.position.y
        );
      }

      if (ir.mode === 'table' && ir.tableId) {
        const root = tableRootsRef.current.get(ir.tableId);
        if (root) {
          p.onDragEnd(ir.tableId, root.position.x, root.position.y);
        }
        dragOverrideRef.current.clear();
      }

      if (ir.mode === 'seat' && ir.tableId && ir.seatNumber !== null) {
        const rect = hostRef.current?.getBoundingClientRect();
        if (rect) {
          const sx = e.clientX - rect.left;
          const sy = e.clientY - rect.top;
          const w = screenToWorld(sx, sy);
          if (ir.seatDragStarted) {
            const target = hitTestPlanner(
              w.x,
              w.y,
              p.tables,
              p.selectedTableIds,
              scaleRef.current
            );
            if (target.kind === 'seat') {
              p.onSeatDrop(target.tableId, target.seatNumber);
            } else {
              p.onSeatDragCancel?.();
            }
          } else {
            p.onSeatClick(ir.tableId, ir.seatNumber);
          }
        }
      }

      // Commit rotation to parent on pointer up
      if (ir.mode === 'rotate' && ir.tableId && ir.tempRot !== null) {
        p.onRotate(ir.tableId, ir.tempRot);
      }

      if (
        (ir.mode === 'resizeCircle' || ir.mode === 'resizeEdge') &&
        ir.tableId &&
        ir.resizeStart &&
        ir.tempSize
      ) {
        const ts = ir.tempSize;
        const pos = dragOverrideRef.current.get(ir.tableId);
        p.onResize(
          ir.tableId,
          ts.width,
          ts.height,
          pos?.x ?? ir.resizeStart.tableX,
          pos?.y ?? ir.resizeStart.tableY
        );
        ir.tempSize = null;
        dragOverrideRef.current.delete(ir.tableId);
      }

      ir.mode = 'idle';
      ir.pointerId = null;
      ir.tableId = null;
      ir.seatNumber = null;
      ir.guestId = null;
      ir.seatDragStartScreen = null;
      ir.seatDragStarted = false;
        ir.resizeStart = null;
        ir.resizeEdge = null;
        ir.resizePointerStartWorld = null;
        ir.tempRot = null;
    },
    [screenToWorld]
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.addEventListener('pointerdown', onPointerDown);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerup', onPointerUp);
    host.addEventListener('pointercancel', onPointerUp);
    return () => {
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerup', onPointerUp);
      host.removeEventListener('pointercancel', onPointerUp);
    };
  }, [onPointerDown, onPointerMove, onPointerUp]);

  /* ---------------------------------------------------------------- */
  /*  Imperative handle (Konva Stage shim for PDF export)              */
  /* ---------------------------------------------------------------- */

  useImperativeHandle(ref, () => {
    const shim: TablePlannerStageShim = {
      container: () => hostRef.current!,
      getPointerPosition: () => pointerRef.current,
      x: (v?: number) => {
        const vp = viewportRef.current;
        if (!vp) return 0;
        if (v !== undefined) {
          vp.position.x = v;
          requestRender();
        }
        return vp.position.x;
      },
      y: (v?: number) => {
        const vp = viewportRef.current;
        if (!vp) return 0;
        if (v !== undefined) {
          vp.position.y = v;
          requestRender();
        }
        return vp.position.y;
      },
      scaleX: (v?: number) => {
        const vp = viewportRef.current;
        if (!vp) return scaleRef.current;
        if (v !== undefined) {
          vp.scale.set(v);
          requestRender();
        }
        return vp.scale.x;
      },
      scaleY: (v?: number) => {
        return shim.scaleX(v);
      },
      toDataURL: (config) => {
        const app = appRef.current;
        if (!app) return '';
        const pr = Math.max(1, config.pixelRatio ?? 1);
        const grid = gridContainerRef.current;
        const floorBg = bgGraphicsRef.current;
        const vp = viewportRef.current;
        const omitGrid = config.omitGrid === true;
        const omitBackground = config.omitBackground === true;
        const frame = config.frame;

        // --- hide grid / floor bg (applies to both paths) ---
        let prevGridVisible: boolean | undefined;
        let prevFloorVisible: boolean | undefined;
        const bgSys = app.renderer.background;
        const prevBgCss = bgSys.color.toRgbaString();
        const prevBgAlpha = bgSys.alpha;
        if (omitGrid && grid) { prevGridVisible = grid.visible; grid.visible = false; }
        if (omitBackground && floorBg) { prevFloorVisible = floorBg.visible; floorBg.visible = false; }
        if (omitBackground) { bgSys.color = '#ffffff'; bgSys.alpha = 1; }

        // --- frame path ---
        // Renders to a RenderTexture of exact outW×outH pixels so we never
        // rely on extract.canvas(stage) which captures the full scene bounds
        // (0→playableSize) rather than the renderer viewport.
        //
        // Viewport at scale=1 so Pixi text renders at its cached resolution
        // (crisp). The final bitmap is then drawn onto an outW×outH canvas,
        // producing a pr-resolution output with only a mild pr/resolution
        // upscale (e.g. 1.5× for pr=3, devicePixelRatio=2).
        if (frame && vp) {
          const prevVpX = vp.position.x;
          const prevVpY = vp.position.y;
          const prevVpScale = vp.scale.x;
          const prevScreenW = propsRef.current.stageWidth;
          const prevScreenH = propsRef.current.stageHeight;
          try {
            const outW = Math.ceil(frame.width * pr);
            const outH = Math.ceil(frame.height * pr);

            // Render at scale=1 into a RenderTexture whose resolution = pr so that
            // 1 plan unit → pr physical pixels in the texture.
            // Using viewport scale=1 keeps text rendered at its cached quality
            // (Pixi text textures are baked at renderer.resolution; using a higher
            // viewport scale just upscales them, causing blur).
            // The texture has rtW × rtH CSS pixels, pr × rtW physical pixels wide →
            // drawn 1:1 onto the outW × outH output canvas (outW = rtW * pr).
            const rtW = Math.ceil(frame.width);
            const rtH = Math.ceil(frame.height);

            vp.scale.set(1);
            vp.position.set(-frame.x, -frame.y);

            const rt = RenderTexture.create({ width: rtW, height: rtH, resolution: pr });
            try {
              (app.renderer as unknown as {
                render: (opts: {
                  container: Container;
                  target: RenderTexture;
                  clear?: boolean;
                }) => void;
              }).render({ container: app.stage, target: rt, clear: true });

              const rExtract = (app.renderer as unknown as {
                extract?: { canvas: (src: RenderTexture) => HTMLCanvasElement };
              }).extract;
              const rendered = rExtract?.canvas(rt) ?? null;

              if (rendered) {
                const oc = document.createElement('canvas');
                oc.width = outW;
                oc.height = outH;
                const ctx = oc.getContext('2d');
                if (ctx) {
                  if (omitBackground) {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, outW, outH);
                  }
                  // rendered is rtW*pr × rtH*pr pixels = outW × outH → 1:1 copy.
                  ctx.drawImage(rendered, 0, 0, outW, outH);
                  return oc.toDataURL(config.mimeType || 'image/png', config.quality ?? 1);
                }
              }
            } finally {
              rt.destroy(true);
            }
          } finally {
            app.renderer.resize(prevScreenW, prevScreenH);
            vp.scale.set(prevVpScale);
            vp.position.set(prevVpX, prevVpY);
            if (omitGrid && grid && prevGridVisible !== undefined) grid.visible = prevGridVisible;
            if (omitBackground && floorBg && prevFloorVisible !== undefined) floorBg.visible = prevFloorVisible;
            if (omitBackground) { bgSys.color = prevBgCss; bgSys.alpha = prevBgAlpha; }
            app.render();
          }
        }

        // --- non-frame path (existing extract-based capture) ---
        if (omitGrid || omitBackground) { app.render(); }
        try {
          const extract = (
            app.renderer as unknown as {
              extract: {
                canvas: (obj: Container) => HTMLCanvasElement;
              };
            }
          ).extract;
          if (extract?.canvas) {
            const c = extract.canvas(app.stage);
            const oc = document.createElement('canvas');
            oc.width = Math.floor(app.renderer.width * pr);
            oc.height = Math.floor(app.renderer.height * pr);
            const ctx = oc.getContext('2d');
            if (ctx) {
              if (omitBackground) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, oc.width, oc.height);
              }
              ctx.scale(pr, pr);
              ctx.drawImage(c, 0, 0);
              return oc.toDataURL(config.mimeType || 'image/png', config.quality ?? 1);
            }
          }
        } catch {
          /* fallback below */
        } finally {
          if (omitGrid && grid && prevGridVisible !== undefined) grid.visible = prevGridVisible;
          if (omitBackground && floorBg && prevFloorVisible !== undefined) floorBg.visible = prevFloorVisible;
          if (omitBackground) { bgSys.color = prevBgCss; bgSys.alpha = prevBgAlpha; }
          if (omitGrid || omitBackground) { app.render(); }
        }
        return (app.canvas as HTMLCanvasElement).toDataURL(
          config.mimeType || 'image/png',
          config.quality ?? 1
        );
      },
    };
    return shim;
  }, [requestRender]);

  return (
    <div
      ref={hostRef}
      className="pixi-table-planner-host"
      style={{
        width: stageWidth,
        height: stageHeight,
        overflow: 'hidden',
        touchAction: 'none',
      }}
    />
  );
});

export default PixiTablePlannerCanvas;
